/** Макс. длина названия категории клиента. */
export const CATEGORY_NAME_LIMIT = 50

/** Палитра цветов по умолчанию для категорий клиента. */
export const categoryColors = [
  '#f6c0b2',
  '#fad2ae',
  '#f9e7ae',
  '#aedbbc',
  '#9ddedb',
  '#a7c7fb',
  '#98c3c8',
  '#d7bcf1',
  '#f6bedb',
  '#f3adaf',
  '#d2d6db',
]

/** Цвет категории клиента по умолчанию (первый из палитры). */
export const DEFAULT_CATEGORY_COLOR = categoryColors[0]

/**
 * Светлый ли фон категории клиента (нужен тёмный текст на плашке).
 * @param {string} color
 * @returns {boolean}
 */
export function isLightCategoryColor(color) {
  const hex = String(color || '').replace('#', '')
  if (hex.length !== 6) return false

  const [r, g, b] = [0, 2, 4].map(
    (i) => parseInt(hex.slice(i, i + 2), 16) / 255
  )

  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6
}

/**
 * Клиент - юрлицо (КА).
 * @param {object|null|undefined} client
 * @returns {boolean}
 */
export function isClientCompany(client) {
  return client?.counteragent?.slug === 'company' || client?.isCompany === true
}

/**
 * Клиент в чёрном списке (ЧС).
 * @param {object|null|undefined} client
 * @returns {boolean}
 */
export function isClientBlacklisted(client) {
  return client?.blacklisted === true
}

/**
 * Есть ли у клиента кастомные категории клиента.
 * @param {object|null|undefined} client
 * @returns {boolean}
 */
export function hasClientCategories(client) {
  return (client?.clientCategories?.length ?? 0) > 0
}

/**
 * Ключ i18n-заметки для КА/ЧС без кастомных категорий клиента.
 * Например `company`, `blacklisted`, `company_blacklisted`.
 * @param {object|null|undefined} client
 * @returns {string|null} ключ или null, если заметка не нужна
 */
export function clientStatusNoteKey(client) {
  const key = [
    isClientCompany(client) && 'company',
    isClientBlacklisted(client) && 'blacklisted',
  ]
    .filter(Boolean)
    .join('_')

  return key || null
}

/**
 * Нужно ли показывать метки клиента (КА / ЧС / категории клиента).
 * @param {object|null|undefined} client
 * @param {{ showCompany?: boolean }} [options]
 * @returns {boolean}
 */
export function hasClientMarks(client, { showCompany = true } = {}) {
  if (!client) return false
  if (isClientBlacklisted(client)) return true
  if (showCompany && isClientCompany(client)) return true

  return hasClientCategories(client)
}

/**
 * Метки на отдельной строке: есть кастомные категории клиента.
 * Только К/А и/или ЧС остаются в одной строке с названием.
 */
export function shouldStackClientMarks(client) {
  return hasClientCategories(client)
}

const USED_KEYS = {
  one: 'pages.clients.categories.used_one',
  few: 'pages.clients.categories.used_few',
  many: 'pages.clients.categories.used_many',
}

/**
 * Форма русского склонения: one / few / many.
 * @param {number} count
 * @returns {'one'|'few'|'many'}
 */
export function pluralForm(count) {
  const n = Math.abs(Number(count) || 0)
  const mod10 = n % 10
  const mod100 = n % 100

  if (mod10 === 1 && mod100 !== 11) return 'one'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few'

  return 'many'
}

/**
 * Подпись «N клиентов» / «1 клиент» с учётом локали.
 * @param {number} count
 * @param {object} i18n Vue i18n instance
 * @returns {string}
 */
export function usedLabel(count, i18n) {
  const n = Number(count) || 0
  const form = i18n.locale === 'mn' ? 'many' : pluralForm(n)

  return i18n.t(USED_KEYS[form], { count: n })
}
